import React from 'react';
import { Box, Typography, IconButton, Tooltip, alpha } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PDVThemeColors } from './PDVThemes';
import { pdvScale, MQ_PHONE } from './pdvScale';

// Zebrado da listagem — a pedido, agora acompanha a cor do tema escolhido
// (colors.primarySolid, ver zebrado logo abaixo) em vez da paleta neutra
// fixa que tinha antes (ver instrucoes_carrinho_pdv_claude.txt). Opacidades
// baixas de propósito: a listagem continua com fundo claro sempre (mesmo em
// tema escuro, ver cartBackground em PDVThemes.ts), então o tom do tema
// entra só como um tingimento sutil, não uma cor sólida — mantém legível
// pra qualquer cor de tema, do ciano claro do SIAC ao roxo/vermelho de
// outros temas.
const COR_LINHA_BRANCA = '#FFFFFF';
const COR_BORDA = 'rgba(0, 0, 0, 0.03)';
const COR_TEXTO_PRINCIPAL = '#1E293B';
const COR_TEXTO_NEUTRO = '#334155';
const COR_TEXTO_SECUNDARIO = '#94A3B8';

// Rotulozinho (QTD/UND/VL.UNIT/TOTAL) que só aparece no celular — lá o
// cabeçalho da tabela some (ver CartItemsList.tsx), então sem isso os
// números da 2ª linha do grid (ver [MQ_PHONE] mais abaixo) ficavam sem
// nenhuma legenda dizendo o que cada um significa.
const LABEL_CELULAR_SX = {
  display: 'none',
  [MQ_PHONE]: {
    display: 'block',
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    color: COR_TEXTO_SECUNDARIO,
  },
};

export interface CartRowItem {
  iditens: string;
  descricao: string;
  ean?: string;
  referencia?: string;
  un: string;
  qtds: number;
  preco: number;
  excluido?: boolean;
}

interface CartItemRowProps {
  index: number;
  item: CartRowItem;
  colors: PDVThemeColors;
  formatQuantityBR: (valor: number) => string;
  formatMoneyBR: (valor: number) => string;
  onRemove: (iditens: string) => void;
}

// Só apresentação — recebe o item já pronto e informa cliques pro pai.
// Nenhuma chamada de API ou regra de negócio aqui.
// React.memo — o Pdv.tsx re-renderiza a cada segundo (relógio do header)
// e a cada venda; sem isso, toda linha do carrinho repintava com ele, sem
// nenhum dado seu ter mudado. Só funciona de fato porque `colors`,
// `formatQuantityBR`, `formatMoneyBR` e `onRemove` (passadas pelo pai) são
// todas memoizadas (useMemo/useCallback) — ver Pdv.tsx/PDVThemes.ts.
export const CartItemRow: React.FC<CartItemRowProps> = React.memo(({
  index, item, colors, formatQuantityBR, formatMoneyBR, onRemove,
}) => {
  const excluido = !!item.excluido;
  const total = item.preco * item.qtds;

  // Zebrado discreto na cor do tema — linha tingida (índice par) e branca
  // (índice ímpar) se alternando.
  const corLinha = index % 2 === 0 ? alpha(colors.primarySolid, 0.07) : COR_LINHA_BRANCA;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        minHeight: pdvScale(65),
        rowGap: 0,
        px: pdvScale(12),
        py: pdvScale(2),
        borderBottom: `1px solid ${COR_BORDA}`,
        // Excluído usa um vermelho bem sutil em vez do fundo padrão, pra
        // ficar claramente diferente sem gritar.
        bgcolor: excluido ? alpha('#EF4444', 0.05) : corLinha,
        opacity: excluido ? 0.75 : 1,
        transition: 'background-color 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          backgroundColor: excluido ? undefined : alpha(colors.primarySolid, 0.14),
        },
        // No celular troca pra CSS Grid com áreas nomeadas em vez de
        // flex-wrap — com flex-wrap, cada linha "decide" sozinha (com base
        // na largura real da descrição) se sobra espaço pra mais um item
        // entrar na mesma linha, então a 2ª linha (Qtd/Und/Vl.Unit/Total)
        // ficava numa posição DIFERENTE em cada item (descrição curta
        // "roubava" a vaga da Qtd pra linha 1, descrição longa não).
        // Grid não tem esse comportamento: cada área sempre cai exatamente
        // onde foi definida, dando o mesmo alinhamento em todas as linhas
        // independente do tamanho da descrição.
        [MQ_PHONE]: {
          display: 'grid',
          gridTemplateColumns: '34px 1fr 1fr 1fr',
          gridTemplateAreas: '"num desc desc desc" "qtd und unit total"',
          alignItems: 'center',
          columnGap: '8px',
          rowGap: '6px',
          minHeight: 'auto',
          py: '10px',
        },
      }}
    >
      {/* Nº — 3 dígitos pra leitura rápida no PDV */}
      <Box sx={{ flex: '0 0 6%', minWidth: pdvScale(34), textAlign: 'center', [MQ_PHONE]: { gridArea: 'num', textAlign: 'left' } }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: pdvScale(17),
            color: excluido ? '#EF4444' : colors.primaryDark,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {String(index + 1).padStart(3, '0')}
        </Typography>
      </Box>

      {/* Descrição + EAN/referência */}
      <Box sx={{ flex: '1 1 49%', minWidth: 0, pl: pdvScale(12), pr: pdvScale(8), [MQ_PHONE]: { gridArea: 'desc', pl: 0 } }}>
        <Typography
          noWrap
          sx={{
            fontWeight: 700,
            fontSize: pdvScale(20),
            color: excluido ? alpha('#EF4444', 0.85) : COR_TEXTO_PRINCIPAL,
            textDecoration: excluido ? 'line-through' : 'none',
          }}
        >
          {item.descricao}
        </Typography>
        {(item.ean || item.referencia) && (
          <Typography noWrap sx={{ fontSize: pdvScale(15), color: COR_TEXTO_SECUNDARIO }}>
            {item.ean || item.referencia}
          </Typography>
        )}
        {excluido && (
          <Typography
            sx={{
              display: 'inline-block',
              mt: pdvScale(2),
              fontSize: pdvScale(11),
              fontWeight: 700,
              letterSpacing: '0.04em',
              color: '#EF4444',
              bgcolor: alpha('#EF4444', 0.12),
              border: `1px solid ${alpha('#EF4444', 0.3)}`,
              borderRadius: 999,
              px: pdvScale(6),
              py: pdvScale(1),
              textTransform: 'uppercase',
            }}
          >
            Excluído
          </Typography>
        )}
      </Box>

      {/* Qtd — minWidth pra nunca ficar menor que o próprio número (ver
          Vl.Unit/Total abaixo pro motivo). */}
      <Box sx={{ flex: '0 0 9%', minWidth: pdvScale(40), textAlign: 'center', [MQ_PHONE]: { gridArea: 'qtd', textAlign: 'left', minWidth: 0 } }}>
        <Typography sx={LABEL_CELULAR_SX}>Qtd</Typography>
        <Typography
          sx={{
            fontSize: pdvScale(19),
            fontWeight: 700,
            color: excluido ? alpha('#EF4444', 0.85) : COR_TEXTO_PRINCIPAL,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatQuantityBR(item.qtds)}
        </Typography>
      </Box>

      {/* Und */}
      <Box sx={{ flex: '0 0 8%', minWidth: pdvScale(36), textAlign: 'center', [MQ_PHONE]: { gridArea: 'und', minWidth: 0 } }}>
        <Typography sx={LABEL_CELULAR_SX}>Und</Typography>
        <Typography
          sx={{
            fontSize: pdvScale(18),
            fontWeight: 500,
            color: COR_TEXTO_NEUTRO,
          }}
        >
          {item.un || ''}
        </Typography>
      </Box>

      {/* Vl. Unit — minWidth (em px, não em %) pra nunca ficar mais estreito
          que o próprio valor formatado (ex.: "11.200,00"). Sem isso, numa
          tela um pouco menor (ou nessa listagem ocupando só ~50% da
          largura, ver CartItemsList.tsx) os 13% viravam poucos pixels, e o
          número transbordava colando no Total do lado — os dois valores
          ficavam "embolados" (ex.: "195,00195,00"), sem espaço nenhum entre
          eles. */}
      <Box sx={{ flex: '0 0 13%', minWidth: pdvScale(78), textAlign: 'right', [MQ_PHONE]: { gridArea: 'unit', minWidth: 0 } }}>
        <Typography sx={LABEL_CELULAR_SX}>Vl. Unit</Typography>
        <Typography
          noWrap
          sx={{
            fontSize: pdvScale(19),
            fontWeight: 600,
            color: excluido ? alpha('#EF4444', 0.85) : COR_TEXTO_NEUTRO,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {formatMoneyBR(item.preco)}
        </Typography>
      </Box>

      {/* Total + lixeira — mesmo motivo do minWidth do Vl.Unit acima
          (espaço garantido pro valor + o botão da lixeira nunca colarem um
          no outro). Lixeira sempre visível (a pedido, ver imagem de
          referência), num botão arredondado com fundo próprio em vez de só
          aparecer no hover. O rótulo "Total" (LABEL_CELULAR_SX) só existe
          visualmente no celular — no desktop fica display:none e não afeta
          nada; column+flex-end aqui não muda o visual desktop porque só
          sobra 1 filho visível (o Box de valor+lixeira) pra alinhar,
          exatamente como antes. */}
      <Box sx={{ flex: '0 0 15%', minWidth: pdvScale(100), display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', [MQ_PHONE]: { gridArea: 'total', minWidth: 0 } }}>
        <Typography sx={LABEL_CELULAR_SX}>Total</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: pdvScale(10) }}>
          <Typography
            noWrap
            sx={{
              fontSize: pdvScale(20),
              fontWeight: 700,
              color: excluido ? '#EF4444' : colors.primaryDark,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatMoneyBR(total)}
          </Typography>
          <Tooltip title={excluido ? 'Item já excluído' : 'Remover item'}>
            <span>
              <IconButton
                size="small"
                disabled={excluido}
                onClick={() => onRemove(item.iditens)}
                sx={{
                  color: excluido ? '#EF4444' : COR_TEXTO_NEUTRO,
                  bgcolor: excluido ? alpha('#EF4444', 0.12) : alpha(colors.primaryDark, 0.08),
                  borderRadius: '10px',
                  p: pdvScale(8),
                  '&:hover': { color: '#fff', backgroundColor: '#EF4444' },
                  '&.Mui-disabled': { color: '#EF4444', backgroundColor: alpha('#EF4444', 0.12) },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: pdvScale(24) }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
});
